import { useState } from "react";
import messages from "messages";
// components
import { ReactComponent as OutlineZipFiles } from "assets/svg/outline-zip-files.svg";
import { ReactComponent as OutlineSuccessfullyUploaded } from "assets/svg/outline-successfully-uploaded.svg";
import { ReactComponent as OutlineCancelUpload } from "assets/svg/outline-cancel-upload.svg";
import { ReactComponent as OutlineLoadingFailed } from "assets/svg/outline-loading-failed.svg";
import ProgressIndicator from "../../../progress-indicator";
import IconButton from "components/icon-button";
import TypeIcon from "../../../type-icon";
// styles
import styles from "./styles.module.scss";
// constants
import { TASK_STATUS, TASK_TYPES } from "../../../../constants";
// utils
import getClasses from "utils/getClasses";
// types
import type { Task } from "../../../..";
// utils
import getMIMETypeByFileName from "utils/getMIMETypeByFileName";
import formatFileSize from "utils/formatFileSize";

type Props = {
  value: Task;
  onCancel: (arg: Task) => void;
  variant: keyof typeof TASK_TYPES;
};

const getCancelledOrFailedMessage = (
  variant: Props["variant"],
  state?: keyof typeof TASK_STATUS,
) => `${messages[variant]} ${state ? messages[state] : ""}`;

const TaskRow = ({ value, onCancel, variant }: Props) => {
  const [showCancel, setShowCancel] = useState<string>();
  return (
    <div
      key={value.id}
      className={styles["task-row"]}
      onMouseEnter={() => {
        if (value.busy) {
          return setShowCancel(value.id);
        }
      }}
      onMouseLeave={() => setShowCancel(undefined)}
    >
      <div
        className={getClasses(
          styles["task-row__icon"],
          value.busy && styles["task-row__icon--dimmed"],
        )}
      >
        {variant === TASK_TYPES.download ? (
          <OutlineZipFiles />
        ) : (
          <TypeIcon
            mimeType={
              value.isFolder ? "" : getMIMETypeByFileName(value.fileName)
            }
            defaultType={value.isFolder ? "folder" : undefined}
          />
        )}
      </div>
      <div className={getClasses(styles["task-row__status"])}>
        <div
          className={getClasses(
            styles["task-row__status-title"],
            variant === TASK_TYPES.download &&
              value.busy &&
              styles["task-row__status-title--dimmed"],
          )}
        >
          {value.state === TASK_STATUS.downloading
            ? messages.downloadingFiles
            : value.state === TASK_STATUS.zipping
            ? messages.zippingFiles
            : value.fileName}
        </div>

        {variant === TASK_TYPES.upload && (
          <div className={styles["task-row__status-subtitle"]}>
            {/* NOTE: for now, all files are uploaded to the root folder, might change in the future */}
            {`${formatFileSize(value.size)}${
              !value.uploadFolderName
                ? ""
                : ` | Uploading to /${value.uploadFolderName}}`
            }`}
          </div>
        )}
      </div>
      <div className={styles["task-row__right-icon"]}>
        {showCancel === value.id ? (
          <IconButton onClick={() => onCancel(value)} disabled={!value.busy}>
            <OutlineCancelUpload />
          </IconButton>
        ) : value.busy ? (
          <ProgressIndicator
            percent={
              // upload task
              variant === TASK_TYPES.upload
                ? value.percent ?? 0
                : // download task
                value.state === TASK_STATUS.downloading
                ? value.percent || 0
                : value.state === TASK_STATUS.zipping
                ? value.percent || 0
                : 0
            }
          />
        ) : value.state === TASK_STATUS.cancelled ||
          value.state === TASK_STATUS.failed ? (
          <>
            <span className={styles["task-row__right-icon-message"]}>
              {getCancelledOrFailedMessage(variant, value.state)}
            </span>
            <OutlineLoadingFailed />
          </>
        ) : (
          <OutlineSuccessfullyUploaded />
        )}
      </div>
    </div>
  );
};

export default TaskRow;
